import { CheckIcon } from "@chakra-ui/icons";
import { chakra } from "@chakra-ui/react";
import { replacePlaceholders } from "../layout/documentLayout";
import {
  choiceOptions,
  firstAnswer,
  optionColumns,
  replaceInAnswer,
  resolveCheckOptions,
  resolveRadioOptions,
  type PatientDocumentAnswer,
} from "./answerFormat";
import { displayedChildValue, exclusiveOptions, parentAnswerFamily } from "./parentAnswer";
import { PRINT_INK_COLOR, TitledBlock } from "./printStyles";
import type { QuestionViewProps } from "./types";

// radio, check, yesNo, dropDown -> the PDF's Radios / Checkboxes blocks. On a template document
// ("list") a child with `parentAnswer` shows what mobile and its editor show: its options plus the
// exclusive one, checked when the parent holds the exclusive value (the template PDF prints the
// exclusive label then, patient-document-processing.utils getDocumentQuestionMappedParentAnswer).
// A GENERAL document ("preview") prints it like any choice (mfDocumentAdapter knows no
// parentAnswer): its own options and stored answer.

const RadioDot = chakra("div", {
  baseStyle: {
    border: "1px solid #c4c4c4",
    borderRadius: "100%",
    boxShadow: "0 0 0 1px #fff inset",
    flex: "none",
    height: "12px",
    width: "12px",
  },
});

const CheckBoxMark = chakra("div", {
  baseStyle: {
    alignItems: "center",
    border: "1px solid #c4c4c4",
    borderRadius: "2px",
    color: "white",
    display: "flex",
    flex: "none",
    height: "12px",
    justifyContent: "center",
    width: "12px",
  },
});

const OptionItem = chakra("div", {
  baseStyle: {
    alignItems: "center",
    display: "flex",
    gap: "12px",
    minWidth: 0,
    hyphens: "auto",
    overflowWrap: "break-word",
  },
});

function OptionMarker(props: { checked: boolean; isCheck: boolean }) {
  if (props.isCheck) {
    return (
      <CheckBoxMark
        bg={props.checked ? PRINT_INK_COLOR : undefined}
        borderColor={props.checked ? PRINT_INK_COLOR : undefined}
      >
        {props.checked && <CheckIcon boxSize="8px" />}
      </CheckBoxMark>
    );
  }
  return <RadioDot bg={props.checked ? PRINT_INK_COLOR : undefined} />;
}

export function ChoiceQuestionView(props: QuestionViewProps) {
  const { answers, block, byId, placeholders } = props;
  const { question } = block;
  const isCheck = question.itemType === "check";
  const isInline = question.answerInline === true;
  const onlyAnswer = question.showOnlyAnswer === true;
  const syncsParent = props.layoutType === "list" && parentAnswerFamily(question, byId) !== null;
  const answer = syncsParent
    ? (displayedChildValue(question, answers, byId) as PatientDocumentAnswer | undefined)
    : firstAnswer(block.answers);

  const resolve = isCheck ? resolveCheckOptions : resolveRadioOptions;
  const options = resolve({
    answer: replaceInAnswer(answer, placeholders),
    options: (syncsParent ? exclusiveOptions(question) : choiceOptions(question)).map((option) =>
      replacePlaceholders(option, placeholders)
    ),
    showOtherOption: question.showOtherOption === true,
  });
  const visible = onlyAnswer ? options.filter((option) => option.checked) : options;
  const columns = optionColumns({
    count: visible.length,
    inDifferentLines: question.possibleAnswersInDifferentLines === true,
    width: block.width,
  });

  const items = visible.map((option, index) => (
    <OptionItem key={`${index}-${option.label}`}>
      {!onlyAnswer && <OptionMarker checked={option.checked} isCheck={isCheck} />}
      <chakra.div minWidth={0}>{option.label}</chakra.div>
    </OptionItem>
  ));

  return (
    <TitledBlock
      gap="8px"
      isInline={isInline}
      title={replacePlaceholders(question.label, placeholders)}
    >
      {isInline ? (
        items
      ) : visible.length === 0 ? null : (
        <chakra.div
          display="grid"
          gap="10px"
          gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
        >
          {items}
        </chakra.div>
      )}
    </TitledBlock>
  );
}
